/**
 * <product-recommendations> preloads Shopify product recommendations.
 *
 * Public contract:
 *   data-url        - Shopify recommendations URL for the current section
 *   data-section-id - section id used to find the matching fetched section
 *
 * The response is fetched before this element reaches the viewport. Its outer
 * wrapper is revealed separately at the viewport so async content never depends
 * on the theme-wide animation setting.
 */

class ProductRecommendations extends HTMLElement {
  #revealObserver = null;
  #revealFrame = null;
  #abortController = null;
  #isLoaded = false;

  connectedCallback() {
    if (this.#isLoaded || !this.dataset.url) return;

    // Already server-rendered — nothing to fetch.
    if (this.innerHTML.trim()) {
      this.#isLoaded = true;
      this.#observeReveal();
      return;
    }

    // Start immediately so the async section is laid out before it reaches the viewport.
    void this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    if (this.#revealFrame) cancelAnimationFrame(this.#revealFrame);
    this.#revealFrame = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #loadRecommendations() {
    if (this.#isLoaded) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const response = await fetch(this.dataset.url, { signal });

      if (!response.ok) {
        throw new Error(`Product recommendations failed: ${response.status}`);
      }

      const text = await response.text();

      if (!this.isConnected) return;

      const html = document.createElement('div');
      html.innerHTML = text;

      const recommendations =
        html.querySelector(
          `product-recommendations[data-section-id="${this.dataset.sectionId}"]`
        ) || html.querySelector('product-recommendations');

      if (!recommendations?.innerHTML.trim()) {
        this.#dismissSection();
        return;
      }

      this.innerHTML = recommendations.innerHTML;
      this.#isLoaded = true;
      this.#observeReveal();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.#dismissSection();
    }
  }

  #observeReveal() {
    const outer = this.closest('.featured-collection__outer-wrapper--async');
    if (!outer) return;

    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    if (this.#revealFrame) cancelAnimationFrame(this.#revealFrame);
    this.#revealFrame = null;

    const reveal = () => {
      this.#revealFrame = requestAnimationFrame(() => {
        outer.classList.add('featured-collection__outer-wrapper--visible');
        this.#revealFrame = null;
      });
    };

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        const shouldReveal = entries.some(
          (entry) =>
            entry.isIntersecting || entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0)
        );
        if (!shouldReveal) return;

        this.#revealObserver?.disconnect();
        this.#revealObserver = null;
        reveal();
      },
      {
        rootMargin: '0px 0px 8% 0px',
        threshold: 0.01,
      }
    );
    this.#revealObserver.observe(outer);
  }

  #dismissSection() {
    const outer = this.closest('.featured-collection__outer-wrapper');
    this.remove();
    if (outer?.childElementCount === 0) {
      outer.remove();
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
